import Link from 'next/link';
import { useEffect, useState } from 'react';

import {
  ACCENT_HEX,
  ARTICLE_TOKENS,
  STICKY_OFFSET_CLASS
} from './design-tokens';
import {
  MiniFixtureTicker,
  useMiniFixtureTickerData
} from '@components/fixtures/v2/MiniFixtureTicker';
import type { ArticleHeading } from '@lib/article-blocks/headings';

// A section counts as active once its heading crosses the top quarter of the
// viewport, which matches where a reader's eye is while scrolling.
const OBSERVER_ROOT_MARGIN = '-84px 0px -75% 0px';

const TOOLS = [
  { label: 'Points Predictor', href: '/predictions' },
  { label: 'Player Comparison', href: '/fpl-player-comparison-tool' },
  { label: 'OPTA Stats', href: '/opta' }
];

const useActiveHeading = (headings: ArticleHeading[]) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (headings.length === 0 || typeof IntersectionObserver === 'undefined')
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: OBSERVER_ROOT_MARGIN }
    );
    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [headings]);

  return activeId;
};

const RailCard = ({ children }: { children: React.ReactNode }) => (
  <div className={`rounded-[14px] border ${ARTICLE_TOKENS.rule}`}>
    {children}
  </div>
);

const RailLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    className={`mb-3 font-body tabular-nums text-[11px] uppercase tracking-[0.1em] ${ARTICLE_TOKENS.muted}`}
  >
    {children}
  </div>
);

const OnThisPage = ({ headings }: { headings: ArticleHeading[] }) => {
  const activeId = useActiveHeading(headings);
  if (headings.length === 0) return null;
  return (
    <RailCard>
      <nav aria-label="On this page" className="px-5 py-[18px]">
        <RailLabel>On this page</RailLabel>
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {headings.map((heading) => (
            <li key={heading.id} className="flex items-baseline gap-[9px]">
              <span
                aria-hidden
                className="mt-1.5 h-[5px] w-[5px] shrink-0 rounded-full"
                style={{
                  background: heading.id === activeId ? ACCENT_HEX : '#d5dade'
                }}
              />
              <a
                href={`#${heading.id}`}
                className={`text-sm leading-[1.35] no-underline hover:text-[#14171c] ${
                  heading.id === activeId
                    ? ARTICLE_TOKENS.ink
                    : ARTICLE_TOKENS.bodyMuted
                }`}
              >
                {heading.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </RailCard>
  );
};

const RailFixtureTicker = () => {
  const { shouldShowTicker } = useMiniFixtureTickerData();

  if (!shouldShowTicker) {
    return null;
  }

  return (
    <RailCard>
      <div
        className={`flex items-center justify-between border-b px-[18px] py-3.5 ${ARTICLE_TOKENS.rule}`}
      >
        <span
          className={`font-heading text-sm font-bold ${ARTICLE_TOKENS.ink}`}
        >
          Fixture ticker
        </span>
        <Link
          href="/fixture-ticker"
          className={`text-xs ${ARTICLE_TOKENS.accentText}`}
        >
          Full ticker
        </Link>
      </div>
      <div className="px-2 pb-2 pt-1">
        <MiniFixtureTicker />
      </div>
    </RailCard>
  );
};

const Tools = () => (
  <RailCard>
    <div className="px-5 py-[18px]">
      <RailLabel>Tools</RailLabel>
      <ul className="m-0 list-none p-0">
        {TOOLS.map((tool) => (
          <li
            key={tool.href}
            className={`border-b last:border-b-0 ${ARTICLE_TOKENS.ruleLight}`}
          >
            <Link
              href={tool.href}
              className={`flex items-center justify-between py-[11px] font-heading text-sm font-semibold no-underline hover:text-[#1f7fc4] ${ARTICLE_TOKENS.ink}`}
            >
              {tool.label}
              <span aria-hidden className={ARTICLE_TOKENS.chrome}>
                →
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </RailCard>
);

type ArticleRailProps = {
  headings: ArticleHeading[];
};

export const ArticleRail = ({ headings }: ArticleRailProps) => (
  <aside
    className={`hidden lg:block lg:sticky ${STICKY_OFFSET_CLASS}`}
    data-cy="article-rail"
  >
    <div className="flex flex-col gap-5">
      <OnThisPage headings={headings} />
      <RailFixtureTicker />
      <Tools />
    </div>
  </aside>
);
